import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { connect } from 'redux-bundler-react';
import { createColumnHelper } from '@tanstack/react-table';
import StepHeader from '@components/step-header/StepHeader';
import ContactFields from '@forms/components/Form/contact-fields/ContactFields';
import { Button } from '@trussworks/react-uswds';
import Icon from '@components/icon/Icon';
import AddOtherPersonsModal from '@forms/components/modals/AddOtherPersonsModal';
import AddAllegedViolatorModal from '@forms/components/modals/AddAllegedViolatorModal';
import SelectInput from '@components/select/Select';
import AgentsActionsTableCell from '@src/app-components/table/tableCellComponents/AgentsActionsTableCell';
import { isObjectNotDirty, formatUSPhoneNumber } from '@src/utils/helpers';
import { ContactTypes, ErrorMessages } from '@src/utils/enums';
import _isEqual from 'lodash/isEqual';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm, FormProvider } from 'react-hook-form';

import { mdiPlusBox, mdiCloseOctagon } from '@mdi/js';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import { contactInputValidation } from '@src/utils/validation/inputValidation.contact';
import TanStackTableBasic from '@src/app-components/table/TanStackTable/TanStackTableBasic';
import { useRequest, useModal } from '@stores';
import { useResetDirtyFields } from '@hooks';
import Section from '@src/app-components/section/Section';

export const ViolationsFormContactsMetadata = {
  sectionName: 'Contact Information',
};

const ViolationsFormContacts = connect(
  'doUpdateViolationComplaintRequestContacts',
  'doUpdateIsAllegedViolatorKnown',
  'selectIsAnonymous',
  'selectRequestAPIData',
  ({
    doUpdateViolationComplaintRequestContacts,
    doUpdateIsAllegedViolatorKnown,
    isAnonymous,
    requestAPIData,
    stepNo,
  }) => {
    const { openModal } = useModal();
    const { isFormReadOnly: isReadOnly, updateSection, isSectionTouched } = useRequest();

    const [otherPersonsRowData, setOtherPersonsRowData] = useState([]);
    const [allegedViolatorRowData, setAllegedViolatorRowData] = useState([]);
    const [otherPersonsTableIsDirty, setOtherPersonsTableIsDirty] = useState(false);
    const [allegedViolatorTableIsDirty, setAllegedViolatorTableIsDirty] = useState(false);

    const prevOtherPersonsRowDataRef = useRef([]);
    const prevAllegedViolatorRowDataRef = useRef([]);
    const allegedViolatorTableLength = allegedViolatorRowData?.length;

    const schema = (allegedViolatorTableLength) =>
      yup.object().shape(
        !isAnonymous
          ? {
              ...contactInputValidation.nameRequired,
              ...contactInputValidation.addressRequired,
              ...contactInputValidation.phoneRequired,
              ...contactInputValidation.faxOptional,
              ...contactInputValidation.emailRequired,
              isAllegedViolatorKnown: yup.string().required(ErrorMessages.SelectOption),
              allegedViolatorTable: yup.boolean().when('isAllegedViolatorKnown', {
                is: (val) => val === 'true' && allegedViolatorTableLength < 1,
                then: () => yup.boolean().required('Alleged Violator: At least one alleged violator is required'),
              }),
            }
          : {
              isAllegedViolatorKnown: yup.string().required('Is Alleged Violator Known?'),
              allegedViolatorTable: yup.boolean().when('isAllegedViolatorKnown', {
                is: (val) => val === 'true' && allegedViolatorTableLength < 1,
                then: () => yup.boolean().required('Alleged Violator: At least one alleged violator is required'),
              }),
            },
        [contactInputValidation.faxOptionalDependencies]
      );

    const reporterContact =
      requestAPIData?.request?.contacts?.length > 0 &&
      requestAPIData?.request?.contacts?.find((contact) => contact.contactType === ContactTypes.Reporter);
    const violationComplaint =
      requestAPIData?.request?.violationComplaints?.length > 0 && requestAPIData?.request?.violationComplaints?.[0];

    const defaultValues = {
      salutation: reporterContact?.salutation ?? null,
      firstName: reporterContact?.firstName ?? null,
      middleName: reporterContact?.middleName ?? null,
      lastName: reporterContact?.lastName ?? null,
      address: reporterContact?.address ?? null,
      addressTwo: reporterContact?.addressTwo ?? null,
      city: reporterContact?.city ?? null,
      state: reporterContact?.state ?? null,
      zipcode: reporterContact?.zipcode ?? null,
      country: reporterContact?.country ?? 'US',
      phoneOneType: reporterContact?.phoneOneType ?? null,
      phoneOneCountryCode: reporterContact?.phoneOneCountryCode ?? '1',
      phoneOne: reporterContact?.phoneOne
        ? reporterContact?.phoneOneCountryCode === '1'
          ? formatUSPhoneNumber(reporterContact.phoneOne)
          : reporterContact?.phoneOne
        : '',
      phoneOneExtension: reporterContact?.phoneOneExtension ?? null,
      phoneTwoType: reporterContact?.phoneTwoType ?? null,
      phoneTwoCountryCode: reporterContact?.phoneTwoCountryCode ?? null,
      phoneTwo: reporterContact?.phoneTwo
        ? reporterContact?.phoneTwoCountryCode === '1'
          ? formatUSPhoneNumber(reporterContact.phoneTwo)
          : reporterContact?.phoneTwo
        : '',
      phoneTwoExtension: reporterContact?.phoneTwoExtension ?? null,
      faxCountryCode: reporterContact?.faxCountryCode ?? '1',
      faxPhone: reporterContact?.faxPhone
        ? reporterContact?.faxCountryCode === '1'
          ? formatUSPhoneNumber(reporterContact.faxPhone)
          : reporterContact?.faxPhone
        : '',
      emailAddress: reporterContact?.emailAddress ?? null,
      company: reporterContact?.company ?? null,
      isAllegedViolatorKnown:
        violationComplaint.isAllegedViolatorKnown === true
          ? 'true'
          : violationComplaint.isAllegedViolatorKnown === false
            ? 'false'
            : '',
    };

    const methods = useForm({
      resolver: yupResolver(schema(allegedViolatorTableLength)),
      mode: 'onBlur',
      defaultValues: defaultValues,
    });
    const {
      formState: { errors, dirtyFields },
      watch,
      setError,
      trigger,
      getValues,
      reset,
    } = methods;
    const isDirty = Object.keys(dirtyFields).length > 0;
    const isValid = Object.keys(errors).length === 0;
    const isTouched = isSectionTouched(stepNo);
    const isAllegedViolatorKnown = watch('isAllegedViolatorKnown');

    useEffect(() => {
      doUpdateIsAllegedViolatorKnown(
        isAllegedViolatorKnown === 'true' ? true : isAllegedViolatorKnown === 'false' ? false : null
      );
    }, [isAllegedViolatorKnown, doUpdateIsAllegedViolatorKnown]);

    useEffect(() => {
      const dirty = isDirty || allegedViolatorTableIsDirty || otherPersonsTableIsDirty;
      updateSection(stepNo, { dirty: dirty, valid: isValid });
    }, [stepNo, isDirty, isValid, allegedViolatorTableIsDirty, otherPersonsTableIsDirty, updateSection]);

    useEffect(() => {
      const tableDataChanged = !_isEqual(allegedViolatorRowData, prevAllegedViolatorRowDataRef.current);
      tableDataChanged && setAllegedViolatorTableIsDirty(true);
    }, [allegedViolatorRowData]);

    useEffect(() => {
      const tableDataChanged = !_isEqual(otherPersonsRowData, prevOtherPersonsRowDataRef.current);
      tableDataChanged && setOtherPersonsTableIsDirty(true);
    }, [otherPersonsRowData]);

    //Reset the dirty states of the fields after a save.
    !isAnonymous && useResetDirtyFields(isTouched, requestAPIData, reset, trigger);

    const columnHelper = createColumnHelper();

    const OtherPersonsColumnDefs = useMemo(
      () => [
        columnHelper.display({
          header: 'Actions',
          id: 'actions',
          cell: ({ row }) => (
            <AgentsActionsTableCell
              row={row}
              rowData={otherPersonsRowData}
              setRowData={setOtherPersonsRowData}
              modalComponent={AddOtherPersonsModal}
              isReadOnly={isReadOnly}
            />
          ),
          size: 60,
          enableSorting: false,
          meta: {
            centerText: true,
          },
        }),
        columnHelper.accessor('firstName', {
          header: 'First Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 200,
        }),
        columnHelper.accessor('middleName', {
          header: 'Middle Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 200,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('lastName', {
          header: 'Last Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 200,
        }),
        columnHelper.accessor('phoneOneType', {
          header: 'Phone One Type',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('phoneOne', {
          header: 'Phone One',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('state', {
          header: 'State',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 75,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('country', {
          header: 'Country',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 75,
          meta: {
            optional: true,
          },
        }),
      ],
      [columnHelper, otherPersonsRowData, isReadOnly]
    );

    const AllegedViolatorColumnDefs = useMemo(
      () => [
        columnHelper.display({
          header: 'Actions',
          id: 'actions',
          cell: ({ row }) => (
            <AgentsActionsTableCell
              row={row}
              rowData={allegedViolatorRowData}
              setRowData={setAllegedViolatorRowData}
              modalComponent={AddAllegedViolatorModal}
              isReadOnly={isReadOnly}
            />
          ),
          size: 60,
          enableSorting: false,
          meta: {
            centerText: true,
          },
        }),
        columnHelper.accessor('contactType', {
          header: 'Contact Type',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
        }),
        columnHelper.accessor('firstName', {
          header: 'First Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
        }),
        columnHelper.accessor('middleName', {
          header: 'Middle Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('lastName', {
          header: 'Last Name',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
        }),
        columnHelper.accessor('phoneOneType', {
          header: 'Phone One Type',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('phoneOne', {
          header: 'Phone One',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('state', {
          header: 'State',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 75,
          meta: {
            optional: true,
          },
        }),
        columnHelper.accessor('country', {
          header: 'Country',
          cell: ({ cell }) => <span>{cell.getValue()}</span>,
          size: 75,
          meta: {
            optional: true,
          },
        }),
      ],
      [columnHelper, allegedViolatorRowData, isReadOnly]
    );

    const addRowData = (data, fnc) => fnc((rowData) => [...rowData, data]);

    const clearRowData = (fnc) => fnc(() => []);

    useEffect(() => {
      if (isTouched) {
        if (isAllegedViolatorKnown === 'true' && allegedViolatorRowData?.length < 1) {
          setError('allegedViolatorTable', {
            type: 'custom',
            message: 'Alleged Violator: At least one alleged violator is required',
          });
        } else {
          trigger('allegedViolatorTable');
        }
      }
    }, [isAllegedViolatorKnown, allegedViolatorRowData?.length, isTouched, setError, trigger]);

    useEffect(() => {
      updateSection(stepNo, { valid: isReadOnly ? true : isValid });
    }, [isValid, isAllegedViolatorKnown, allegedViolatorRowData, updateSection, stepNo, isReadOnly]);

    //Load tables from database
    useEffect(() => {
      const otherContacts = requestAPIData?.request?.violationComplaints?.[0]?.contacts?.filter(
        (contact) => contact.contactType === ContactTypes.Witness
      );
      const suspects = requestAPIData?.request?.violationComplaints?.[0]?.contacts?.filter(
        (contact) => contact.contactType !== ContactTypes.Witness && contact.contactType !== ContactTypes.Reporter
      );

      otherContacts && setOtherPersonsRowData(otherContacts);
      prevOtherPersonsRowDataRef.current = otherContacts;
      setOtherPersonsTableIsDirty(false);
      suspects && setAllegedViolatorRowData(suspects);
      prevAllegedViolatorRowDataRef.current = suspects;
      setAllegedViolatorTableIsDirty(false);
      isTouched && trigger();
    }, [requestAPIData, isTouched, trigger]);

    useEffect(() => {
      // Format contacts array
      const latestvalues = getValues();
      const { ...reporterFields } = latestvalues;
      const reporterFieldsWithIDs = {
        ...reporterFields,
        contactID: reporterContact?.contactID ?? undefined,
        requestID: reporterContact?.requestID ?? undefined,
        version: reporterContact?.version ?? undefined,
        contactType: ContactTypes.Reporter,
        createdBy: reporterContact?.createdBy ?? undefined,
        phoneOne: reporterFields?.phoneOne?.replace(/\D/g, '') ?? undefined,
        phoneTwo: reporterFields?.phoneTwo?.replace(/\D/g, '') ?? undefined,
        faxPhone: reporterFields?.faxPhone?.replace(/\D/g, '') ?? undefined,
      };
      const reporterObj = isAnonymous ? [] : [reporterFieldsWithIDs];
      // Only include alleged violators if isAllegedViolatorKnown is 'true'
      const violatorsToInclude = isAllegedViolatorKnown === 'true' ? allegedViolatorRowData : [];
      const contactArr = [...reporterObj, ...otherPersonsRowData, ...violatorsToInclude];
      // Update Contacts array state value
      isObjectNotDirty(reporterFields) && doUpdateViolationComplaintRequestContacts(contactArr);
    }, [
      otherPersonsRowData,
      allegedViolatorRowData,
      isAllegedViolatorKnown,
      getValues,
      doUpdateViolationComplaintRequestContacts,
      isAnonymous,
      reporterContact,
    ]);

    const handleFieldChange = useCallback(() => {
      const latestvalues = getValues();
      const { ...reporterFields } = latestvalues;
      const reporterFieldsWithIDs = {
        ...reporterFields,
        contactID: reporterContact?.contactID ?? undefined,
        requestID: reporterContact?.requestID ?? undefined,
        version: reporterContact?.version ?? undefined,
        contactType: ContactTypes.Reporter,
        createdBy: reporterContact?.createdBy ?? undefined,
        phoneOne: reporterFields?.phoneOne?.replace(/\D/g, '') ?? undefined,
        phoneTwo: reporterFields?.phoneTwo?.replace(/\D/g, '') ?? undefined,
        faxPhone: reporterFields?.faxPhone?.replace(/\D/g, '') ?? undefined,
      };
      const reporterObj = isAnonymous ? [] : [reporterFieldsWithIDs];
      // Use the watched value instead of destructuring from getValues
      const violatorsToInclude = isAllegedViolatorKnown === 'true' ? allegedViolatorRowData : [];
      const contactArr = [...reporterObj, ...otherPersonsRowData, ...violatorsToInclude];
      doUpdateViolationComplaintRequestContacts(contactArr);
    }, [
      allegedViolatorRowData,
      doUpdateViolationComplaintRequestContacts,
      getValues,
      isAllegedViolatorKnown,
      isAnonymous,
      otherPersonsRowData,
      reporterContact.contactID,
      reporterContact.createdBy,
      reporterContact.requestID,
      reporterContact.version,
    ]);

    useEffect(() => {
      trigger();
    }, [trigger]);

    const onViolatorAddClick = () =>
      openModal(AddAllegedViolatorModal, {
        addRowData: addRowData,
        setRowData: setAllegedViolatorRowData,
        isReadOnly: isReadOnly,
      });

    const onOtherPersonsAddClick = () =>
      openModal(AddOtherPersonsModal, {
        addRowData: addRowData,
        setRowData: setOtherPersonsRowData,
        isReadOnly: isReadOnly,
      });

    return (
      <FormProvider {...methods}>
        {errors && isTouched && !isReadOnly && <ErrorSummary errors={errors} sectionNo={stepNo} />}
        <StepHeader text='Contact Information' />
        {!isAnonymous && (
          <ContactFields label='Reporter' showButton isReadOnly={isReadOnly} handleFieldChange={handleFieldChange} />
        )}
        <Section label='Alleged Violator'>
          <div id='allegedViolatorTable'>
            <SelectInput
              fullWidth={false}
              className='width-50'
              name='isAllegedViolatorKnown'
              label='Is Alleged Violator known?'
              required
              readOnly={isReadOnly}
              onChange={handleFieldChange}
            >
              <option key='2' value='true'>
                Yes
              </option>
              <option key='3' value='false'>
                No
              </option>
            </SelectInput>
          </div>
          {isAllegedViolatorKnown === 'true' && (
            <>
              <div className='display-flex margin-top-2 padding-bottom-2 flex-justify-end'>
                {allegedViolatorRowData.length > 0 && (
                  <Button
                    className='add-contact-button'
                    title='Add New Row'
                    size='small'
                    onClick={() =>
                      openModal(AddAllegedViolatorModal, {
                        addRowData: addRowData,
                        setRowData: setAllegedViolatorRowData,
                        isReadOnly: isReadOnly,
                      })
                    }
                    disabled={isReadOnly}
                  >
                    <Icon focusable={false} className='margin-right-1' path={mdiPlusBox} size={'16px'} />
                    Add New Row
                  </Button>
                )}
                {allegedViolatorRowData.length !== 0 && (
                  <Button
                    className='clear-table-button'
                    title='Remove Rows'
                    size='small'
                    onClick={() => clearRowData(setAllegedViolatorRowData)}
                    disabled={isReadOnly || allegedViolatorRowData?.length === 0}
                  >
                    <Icon focusable={false} className='margin-right-1' path={mdiCloseOctagon} size={'16px'} />
                    Remove Rows
                  </Button>
                )}
              </div>
              <div className=' margin-bottom-2'>
                <TanStackTableBasic
                  data={allegedViolatorRowData}
                  columns={AllegedViolatorColumnDefs}
                  onAddClick={onViolatorAddClick}
                  placeholderText='No contacts found.'
                  isReadOnly={isReadOnly}
                />
              </div>
            </>
          )}
        </Section>
        <Section label='Additional Contacts or Witnesses'>
          <p className='margin-top-2'>
            Other persons that may have information regarding the alleged violation (provide name and contact
            information if available):
          </p>
          <div className='display-flex  margin-top-2 padding-bottom-2 flex-justify-end' id='otherPersonsTable'>
            {otherPersonsRowData?.length > 0 && (
              <Button
                className='add-contact-button'
                title='Add New Row'
                size='small'
                onClick={() =>
                  openModal(AddOtherPersonsModal, {
                    addRowData: addRowData,
                    setRowData: setOtherPersonsRowData,
                    isReadOnly: isReadOnly,
                  })
                }
                disabled={isReadOnly}
              >
                <Icon focusable={false} className='margin-right-1' path={mdiPlusBox} size={'16px'} />
                Add New Row
              </Button>
            )}
            {otherPersonsRowData?.length !== 0 && (
              <Button
                className='clear-table-button'
                title='Remove Rows'
                size='small'
                onClick={() => clearRowData(setOtherPersonsRowData)}
                disabled={isReadOnly || otherPersonsRowData?.length === 0}
              >
                <Icon focusable={false} className='margin-right-1' path={mdiCloseOctagon} size={'16px'} />
                Remove Rows
              </Button>
            )}
          </div>
          <div className=' margin-bottom-2'>
            <TanStackTableBasic
              data={otherPersonsRowData}
              columns={OtherPersonsColumnDefs}
              onAddClick={onOtherPersonsAddClick}
              placeholderText='No contacts found.'
              isReadOnly={isReadOnly}
            />
          </div>
        </Section>
      </FormProvider>
    );
  }
);
ViolationsFormContacts.metadata = ViolationsFormContactsMetadata;

export default ViolationsFormContacts;
